import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/hooks/use-toast';

const LoanCalculator = () => {
  const [loanAmount, setLoanAmount] = useState<number>(10000);
  const [interestRate, setInterestRate] = useState<number>(5);
  const [loanTerm, setLoanTerm] = useState<number>(3);
  const [paymentFrequency, setPaymentFrequency] = useState<string>("monthly");
  
  const [payment, setPayment] = useState<number>(0);
  const [totalPayments, setTotalPayments] = useState<number>(0);
  const [totalInterest, setTotalInterest] = useState<number>(0);
  
  // Add a ref to the calculator container
  const calculatorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (loanAmount > 0 && interestRate > 0 && loanTerm > 0) {
      const periodicRate = getPeriodicRate();
      const numberOfPayments = getNumberOfPayments();
      
      // Payment calculation using the formula: P = L[c(1 + c)^n]/[(1 + c)^n - 1]
      const paymentValue = loanAmount * 
        (periodicRate * Math.pow(1 + periodicRate, numberOfPayments)) / 
        (Math.pow(1 + periodicRate, numberOfPayments) - 1);
      
      setPayment(paymentValue);
      setTotalPayments(paymentValue * numberOfPayments);
      setTotalInterest(paymentValue * numberOfPayments - loanAmount);
    } else {
      setPayment(0);
      setTotalPayments(0);
      setTotalInterest(0);
    }
  }, [loanAmount, interestRate, loanTerm, paymentFrequency]);

  // Prevent scrolling to bottom when elements get focus
  useEffect(() => {
    // Function to prevent scroll on focus
    const preventScrollOnFocus = (e: Event) => {
      e.preventDefault();
      // Keep focus but prevent scrolling
      if (calculatorRef.current) {
        calculatorRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    };

    // Get all focusable elements within the calculator
    const focusableElements = calculatorRef.current?.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    // Add event listeners to prevent scroll
    focusableElements?.forEach((el) => {
      el.addEventListener('focus', preventScrollOnFocus);
    });

    // Clean up
    return () => {
      focusableElements?.forEach((el) => {
        el.removeEventListener('focus', preventScrollOnFocus);
      });
    };
  }, [calculatorRef]); // Re-apply when ref changes

  const getPeriodicRate = () => {
    const annualRate = interestRate / 100;
    switch(paymentFrequency) {
      case "weekly":
        return annualRate / 52;
      case "biweekly":
        return annualRate / 26;
      case "monthly":
      default:
        return annualRate / 12;
    }
  };

  const getNumberOfPayments = () => {
    switch(paymentFrequency) {
      case "weekly":
        return loanTerm * 52;
      case "biweekly":
        return loanTerm * 26;
      case "monthly":
      default:
        return loanTerm * 12;
    }
  };

  const getFrequencyLabel = () => {
    switch(paymentFrequency) {
      case "weekly":
        return "Week";
      case "biweekly":
        return "Two Weeks";
      case "monthly":
      default:
        return "Month";
    }
  };
  
  const handleLoanAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value) || 0;
    setLoanAmount(value);
    
    // Show toast notification when value is updated
    toast({
      title: "Loan Amount Updated",
      description: `Loan amount set to ${formatCurrency(value)}`,
    });
  };

  const handleInterestRateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value) || 0;
    const limitedValue = Math.min(Math.max(value, 0), 50); // Limit interest rate between 0 and 50%
    setInterestRate(limitedValue);
    
    // Show toast notification when value is updated
    if (value !== limitedValue) {
      toast({
        title: "Interest Rate Limited",
        description: "Interest rate must be between 0% and 50%",
        variant: "destructive"
      });
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  return (
    <div className="max-w-3xl mx-auto" ref={calculatorRef}>
      <div className="grid md:grid-cols-2 gap-8">
        {/* Input Section */}
        <Card className="border border-gray-200 shadow-sm">
          <CardContent className="p-6">
            <h2 className="text-xl font-semibold mb-4">Enter Loan Details</h2>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="loan-amount" className="font-medium text-gray-700">Loan Amount ($)</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <Input
                    id="loan-amount"
                    type="number"
                    min="0"
                    step="100"
                    value={loanAmount}
                    onChange={handleLoanAmountChange}
                    className="pl-8 bg-[#F5F5F5] border border-[#D0D0D0] focus:border-[#0066CC] focus:bg-white"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="interest-rate" className="font-medium text-gray-700">Interest Rate (% per year)</Label>
                <div className="relative">
                  <Input
                    id="interest-rate"
                    type="number"
                    min="0"
                    max="50"
                    step="0.01"
                    value={interestRate}
                    onChange={handleInterestRateChange}
                    className="pr-8 bg-[#F5F5F5] border border-[#D0D0D0] focus:border-[#0066CC] focus:bg-white"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">%</span>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="loan-term" className="font-medium text-gray-700">Loan Term (Years)</Label>
                <Input
                  id="loan-term"
                  type="number"
                  min="1"
                  max="30"
                  step="1"
                  value={loanTerm}
                  onChange={(e) => setLoanTerm(parseInt(e.target.value) || 1)}
                  className="bg-[#F5F5F5] border border-[#D0D0D0] focus:border-[#0066CC] focus:bg-white"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="payment-frequency" className="font-medium text-gray-700">Payment Frequency</Label>
                <Select
                  value={paymentFrequency}
                  onValueChange={setPaymentFrequency}
                >
                  <SelectTrigger id="payment-frequency" className="bg-[#F5F5F5] border border-[#D0D0D0] focus:border-[#0066CC] focus:bg-white">
                    <SelectValue placeholder="Select frequency" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="biweekly">Bi-Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Results Section */}
        <Card className="border border-gray-200 shadow-sm">
          <CardContent className="p-6">
            <h2 className="text-xl font-semibold mb-4">Results</h2>
            
            <Table>
              <TableBody>
                <TableRow className="bg-[#E6F3FF]">
                  <TableCell className="font-medium text-gray-700">Payment Every {getFrequencyLabel()}</TableCell>
                  <TableCell className="text-right font-bold">{formatCurrency(payment)}</TableCell>
                </TableRow>
                <TableRow className="bg-white">
                  <TableCell className="font-medium text-gray-700">Total of {getNumberOfPayments()} Payments</TableCell>
                  <TableCell className="text-right font-bold">{formatCurrency(totalPayments)}</TableCell>
                </TableRow>
                <TableRow className="bg-[#E6F3FF]">
                  <TableCell className="font-medium text-gray-700">Total Interest</TableCell>
                  <TableCell className="text-right font-bold">{formatCurrency(totalInterest)}</TableCell>
                </TableRow>
                <TableRow className="bg-white">
                  <TableCell className="font-medium text-gray-700">Loan Amount</TableCell>
                  <TableCell className="text-right font-bold">{formatCurrency(loanAmount)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LoanCalculator;
